import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  advisoryPeriodFactPath,
  listenToAdvisoryPeriodFact,
  validateAdvisoryPeriodFactPath,
  type AdvisoryRawDoc,
} from '../../services/advisoryService';
import { advisoryTimestampToIso } from './useAdvisoryAnswers';
import { validateReportingPeriodV01, type ReportingPeriodV01 } from './advisoryReportingPeriod';

export interface AdvisoryPeriodFactV01 {
  period: ReportingPeriodV01;
  confirmed: boolean;
  defaulted: boolean;
  extractionFailed: boolean;
  extractedAt: string | null;
  confirmedAt: string | null;
}

export type AdvisoryPeriodFactValidation =
  | { ok: true; value: AdvisoryPeriodFactV01 }
  | { ok: false; issue: 'invalid-period-fact' };

const validTimestamp = (value: unknown): string | null | undefined => {
  if (value === undefined || value === null || value === '') return null;
  const iso = advisoryTimestampToIso(value);
  return iso && !Number.isNaN(Date.parse(iso)) ? iso : undefined;
};

/** Maps server-owned fact fields while reducing the backend error to one safe boolean. */
export function mapAdvisoryPeriodFact(raw: AdvisoryRawDoc): AdvisoryPeriodFactValidation {
  const period = validateReportingPeriodV01({
    year: raw.data.year,
    quarter: raw.data.quarter,
    endQuarter: raw.data.end_quarter,
    yearType: raw.data.year_type,
    fiscalStartMonth: raw.data.fiscal_start_month,
  });
  const confirmed = raw.data.confirmed;
  const defaulted = raw.data.defaulted;
  const extractedAt = validTimestamp(raw.data.extractedAt);
  const confirmedAt = validTimestamp(raw.data.confirmedAt);

  if (
    !period.ok ||
    typeof confirmed !== 'boolean' ||
    typeof defaulted !== 'boolean' ||
    extractedAt === undefined ||
    confirmedAt === undefined ||
    (confirmed && confirmedAt === null)
  ) {
    return { ok: false, issue: 'invalid-period-fact' };
  }

  return {
    ok: true,
    value: {
      period: period.value,
      confirmed,
      defaulted,
      extractionFailed: typeof raw.data.error === 'string' && raw.data.error.trim().length > 0,
      extractedAt,
      confirmedAt,
    },
  };
}

export type AdvisoryPeriodFactStatus =
  | 'disabled'
  | 'loading'
  | 'ready'
  | 'absent'
  | 'denied'
  | 'failed'
  | 'invalid';

export interface UseAdvisoryPeriodFactOptions {
  enabled: boolean;
  userId?: string;
  projectId?: string;
  confirmedFactPath?: unknown;
}

export interface UseAdvisoryPeriodFactResult {
  status: AdvisoryPeriodFactStatus;
  fact: AdvisoryPeriodFactV01 | null;
  errorCode: string | null;
  retry: () => void;
}

interface PeriodFactState {
  status: AdvisoryPeriodFactStatus;
  fact: AdvisoryPeriodFactV01 | null;
  errorCode: string | null;
}

const DISABLED: PeriodFactState = { status: 'disabled', fact: null, errorCode: null };
const isPermissionDenied = (code: string): boolean =>
  code === 'permission-denied' || code === 'firestore/permission-denied';

export function useAdvisoryPeriodFact({
  enabled,
  userId,
  projectId,
  confirmedFactPath,
}: UseAdvisoryPeriodFactOptions): UseAdvisoryPeriodFactResult {
  const [state, setState] = useState<PeriodFactState>(DISABLED);
  const [retryVersion, setRetryVersion] = useState(0);
  const path = useMemo(() => {
    if (!enabled || !userId || !projectId) return null;
    return (
      validateAdvisoryPeriodFactPath(confirmedFactPath, userId, projectId) ??
      advisoryPeriodFactPath(userId, projectId)
    );
  }, [confirmedFactPath, enabled, projectId, userId]);

  useEffect(() => {
    if (!enabled || !path) {
      setState(DISABLED);
      return undefined;
    }

    let live = true;
    setState({ status: 'loading', fact: null, errorCode: null });
    const subscription = listenToAdvisoryPeriodFact(
      path,
      document => {
        if (!live) return;
        if (!document) {
          setState({ status: 'absent', fact: null, errorCode: null });
          return;
        }
        const mapped = mapAdvisoryPeriodFact(document);
        setState(
          mapped.ok
            ? { status: 'ready', fact: mapped.value, errorCode: null }
            : { status: 'invalid', fact: null, errorCode: null }
        );
      },
      errorCode => {
        if (!live) return;
        setState({
          status: isPermissionDenied(errorCode) ? 'denied' : 'failed',
          fact: null,
          errorCode,
        });
      }
    );
    const unsubscribe = typeof subscription === 'function' ? subscription : () => undefined;
    return () => {
      live = false;
      unsubscribe();
    };
  }, [enabled, path, retryVersion]);

  const retry = useCallback(() => setRetryVersion(version => version + 1), []);
  return { ...state, retry };
}
